import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicationsTable from "@/components/PublicationsTable";
import { STATUSES, PLATFORMS, FORMATS } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Contenus({ embedded = false }) {
  const [pubs, setPubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = {
    client: searchParams.get("client") || "", status: searchParams.get("status") || "",
    platform: searchParams.get("platform") || "", format: searchParams.get("format") || "",
  };
  const setFilters = (f) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    for (const k of ["client", "status", "platform", "format"]) {
      if (f[k]) n.set(k, f[k]); else n.delete(k);
    }
    return n;
  }, { replace: true });
  const sortDesc = searchParams.get("sort") === "desc";
  const setSortDesc = (v) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set("sort", "desc"); else n.delete("sort");
    return n;
  }, { replace: true });
  const navigate = useNavigate();

  const load = () => api.get("/publications").then((r) => setPubs(r.data)).catch(() => {});
  const [confirmResend, setConfirmResend] = useState(false);
  const [resending, setResending] = useState(false);

  const bulkResend = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/publications/bulk-resend", { ids: modifPubs.map((p) => p.id) });
      toast.success(
        `${data.resent} publication(s) passée(s) en « À faire valider »`,
        data.emails_sent ? { description: `${data.emails_sent} email(s) envoyé(s) au(x) client(s)` } : undefined);
      setConfirmResend(false);
      load();
    } catch { toast.error("Erreur lors du renvoi groupé"); }
    finally { setResending(false); }
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    // Vignettes à jour dès qu'une publication est modifiée ailleurs
    const h = () => load();
    window.addEventListener("sf:pubs", h);
    return () => window.removeEventListener("sf:pubs", h);
  }, []);

  const rows = useMemo(() => {
    let out = pubs.filter((p) =>
      (!filters.client || p.client_id === filters.client) &&
      (!filters.status || p.status === filters.status) &&
      (!filters.platform || p.platform === filters.platform) &&
      (!filters.format || p.format === filters.format));
    out.sort((a, b) => sortDesc
      ? (b.date + (b.time || "")).localeCompare(a.date + (a.time || ""))
      : (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
    return out;
  }, [pubs, filters, sortDesc]);

  const modifPubs = rows.filter((p) => p.status === "modification");

  const sel = "text-sm border border-neutral-200 rounded-md px-2.5 py-1.5 bg-white";

  return (
    <div className="space-y-5" data-testid="contenus-page">
      {!embedded && (
      <div>
        <h1 className="font-heading text-3xl font-bold">Contenus</h1>
        <p className="text-neutral-500 text-sm mt-1">Toutes les publications sous forme de tableau.</p>
      </div>
      )}
      <div className="flex flex-wrap gap-2">
        <select className={sel} data-testid="ct-filter-client" value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })}>
          <option value="">Tous les clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={sel} data-testid="ct-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">Tous statuts</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className={sel} data-testid="ct-filter-platform" value={filters.platform} onChange={(e) => setFilters({ ...filters, platform: e.target.value })}>
          <option value="">Toutes plateformes</option>
          {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select className={sel} data-testid="ct-filter-format" value={filters.format} onChange={(e) => setFilters({ ...filters, format: e.target.value })}>
          <option value="">Tous formats</option>
          {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <button className={`${sel} hover:bg-neutral-50`} data-testid="ct-sort" onClick={() => setSortDesc(!sortDesc)}>
          Date {sortDesc ? "↓" : "↑"}
        </button>
        {modifPubs.length > 0 && (
          <button data-testid="ct-bulk-resend-btn" onClick={() => setConfirmResend(true)}
            className="flex items-center gap-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 transition-colors ml-auto">
            <RefreshCw className="w-3.5 h-3.5" />
            Renvoyer {modifPubs.length} modification(s) en validation
          </button>
        )}
      </div>
      <AlertDialog open={confirmResend} onOpenChange={setConfirmResend}>
        <AlertDialogContent data-testid="ct-bulk-resend-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Renvoyer {modifPubs.length} publication(s) en validation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Toutes les publications au statut « Modification demandée »{filters.client ? " (client filtré)" : ""} passeront
              directement en « À faire valider » et le client sera notifié (email si son adresse est renseignée).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ct-bulk-resend-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="ct-bulk-resend-confirm-btn" onClick={bulkResend} disabled={resending}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {resending ? "Envoi…" : "Confirmer l'envoi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PublicationsTable pubs={rows} showClient clients={clients}
        onOpen={(p) => navigate(`/publications/${p.id}`)} onChanged={load} testPrefix="ct" />
    </div>
  );
}
